import { useState } from 'react';
import ReportsNav from '../../components/ReportsNav';
import { reportsApi, type FinancialStatementsDto } from '../../api/reports';
import MoneyCell from '../../components/MoneyCell';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function FinancialStatements() {
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<FinancialStatementsDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await reportsApi.financialStatements({ from, to });
      setData(result);
    } catch (e: any) {
      setError(e.message || 'Failed to generate financial statements');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <ReportsNav />
      <h1>Finansinės ataskaitos</h1>

      <div className="filters" style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
        <label>
          Nuo: <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </label>
        <label>
          Iki: <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </label>
        <button onClick={handleGenerate} disabled={loading}>
          {loading ? <LoadingSpinner /> : 'Generate'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          {/* Income Statement */}
          <div className="card">
            <h2>Pelno (nuostolio) ataskaita</h2>
            <p style={{ color: '#666' }}>Period: {data.incomeStatement.periodFrom} to {data.incomeStatement.periodTo}</p>

            <h3 style={{ color: 'green' }}>Pajamos (5 klasė)</h3>
            <table className="data-table">
              <thead>
                <tr><th>Sąskaita</th><th>Pavadinimas</th><th style={{ textAlign: 'right' }}>Amount</th></tr>
              </thead>
              <tbody>
                {data.incomeStatement.incomeLines.map((line, i) => (
                  <tr key={i}>
                    <td>{line.accountNumber}</td>
                    <td>{line.accountName}</td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={line.amount} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={2}><strong>Pajamos iš viso</strong></td>
                  <td style={{ textAlign: 'right' }}><strong><MoneyCell value={data.incomeStatement.totalIncome} /></strong></td>
                </tr>
              </tfoot>
            </table>

            <h3 style={{ color: 'red', marginTop: '1rem' }}>Sąnaudos (6 klasė)</h3>
            <table className="data-table">
              <thead>
                <tr><th>Sąskaita</th><th>Pavadinimas</th><th style={{ textAlign: 'right' }}>Amount</th></tr>
              </thead>
              <tbody>
                {data.incomeStatement.expenseLines.map((line, i) => (
                  <tr key={i}>
                    <td>{line.accountNumber}</td>
                    <td>{line.accountName}</td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={line.amount} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={2}><strong>Sąnaudos iš viso</strong></td>
                  <td style={{ textAlign: 'right' }}><strong><MoneyCell value={data.incomeStatement.totalExpenses} /></strong></td>
                </tr>
              </tfoot>
            </table>

            <div style={{ marginTop: '1rem', padding: '0.5rem', backgroundColor: data.incomeStatement.netProfit >= 0 ? '#d4edda' : '#f8d7da', borderRadius: '4px' }}>
              <strong>Net {data.incomeStatement.netProfit >= 0 ? 'Profit' : 'Loss'}: <MoneyCell value={Math.abs(data.incomeStatement.netProfit)} /></strong>
            </div>
          </div>

          {/* Balansas */}
          <div className="card">
            <h2>Balansas</h2>
            <p style={{ color: '#666' }}>As of: {data.balanceSheet.asOfDate}</p>

            {data.balanceSheet.sections.map((section, si) => (
              <div key={si} style={{ marginBottom: '1rem' }}>
                <h3>{section.sectionName}</h3>
                <table className="data-table">
                  <thead>
                    <tr><th>Sąskaita</th><th>Pavadinimas</th><th style={{ textAlign: 'right' }}>Balance</th></tr>
                  </thead>
                  <tbody>
                    {section.lines.map((line, li) => (
                      <tr key={li}>
                        <td>{line.accountNumber || '(auto)'}</td>
                        <td>{line.accountName}</td>
                        <td style={{ textAlign: 'right' }}><MoneyCell value={line.netBalance} /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td colSpan={2}><strong>Total {section.sectionName}</strong></td>
                      <td style={{ textAlign: 'right' }}><strong><MoneyCell value={section.total} /></strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}

            <div style={{ marginTop: '1rem', padding: '0.5rem', backgroundColor: '#f0f4fa', borderRadius: '4px' }}>
              <div><strong>Ilgalaikis turtas ir kt. — Aktyvas iš viso:</strong> <MoneyCell value={data.balanceSheet.totalAssets} /></div>
              <div><strong>Nuosavas kapitalas ir įsipareigojimai iš viso:</strong> <MoneyCell value={data.balanceSheet.totalEquityAndLiabilities} /></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
